import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, X, AlertCircle } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';

interface NewMVP {
  mvpId: string;
  name: string;
  description: string;
  category: string;
  targetAudience: string;
  createdFrom?: string;
  tags: string[];
  status: string;
  notificationEmail?: string;
}

interface ApiSuccess {
  status: 'success';
  message: string;
  data: {
    mvpId: string;
    name: string;
    owner: string;
    createdAt: string;
  };
}

interface ApiError {
  status: 'error';
  message: string;
  error?: {
    message: string;
    stack?: string;
  };
}

type ApiResponse = ApiSuccess | ApiError;

const API_URL = import.meta.env.VITE_API_BASE_URL || '';

const STATUS_OPTIONS = [
  { value: 'activo', label: 'Activo' },
  { value: 'inactivo', label: 'Inactivo' },
  { value: 'borrador', label: 'Borrador' },
  { value: 'archivado', label: 'Archivado' }
];

const CATEGORY_OPTIONS = [
  { value: 'SaaS B2B', label: 'SaaS B2B' },
  { value: 'SaaS B2C', label: 'SaaS B2C' },
  { value: 'Marketplace', label: 'Marketplace' },
  { value: 'E-commerce', label: 'E-commerce' },
  { value: 'Mobile App', label: 'Mobile App' },
  { value: 'Landing Page', label: 'Landing Page' },
  { value: 'Otro', label: 'Otro' }
];

export default function NewMVP() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<NewMVP>({
    mvpId: '',
    name: '',
    description: '',
    category: '',
    targetAudience: '',
    createdFrom: '',
    tags: [],
    status: 'activo',
    notificationEmail: ''
  });

  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);

  // Create MVP mutation
  const createMutation = useMutation({
    mutationFn: async (data: NewMVP) => {
      const token = sessionStorage.getItem('token');
      
      // Asegurar que los datos cumplan con el formato requerido
      const payload = {
        mvpId: data.mvpId.toLowerCase().trim(),
        name: data.name.trim(),
        description: data.description.trim(),
        category: data.category.trim(),
        targetAudience: data.targetAudience.trim(),
        createdFrom: data.createdFrom?.trim() || undefined,
        notificationEmail: data.notificationEmail?.trim() || undefined,
        tags: data.tags.map(tag => tag.trim()).filter(Boolean),
        status: data.status.toLowerCase()
      };

      const response = await fetch(`${API_URL}/mvps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      
      const responseData = await response.json() as ApiResponse;
      
      if (!response.ok || responseData.status === 'error') {
        throw new Error(
          responseData.status === 'error'
            ? responseData.error?.message || responseData.message
            : 'Error al crear el MVP'
        );
      }
      
      return responseData;
    },
    onSuccess: (response) => {
      setApiError(null);
      queryClient.invalidateQueries({ queryKey: ['mvps'] });
      
      // Mostrar mensaje de éxito
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50';
      successMessage.innerHTML = `
        <svg class="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <div>
          <p class="font-medium">${response.message}</p>
          <p class="text-sm text-emerald-600">MVP ID: ${response.data.mvpId}</p>
        </div>
      `;
      document.body.appendChild(successMessage);
      
      // Remover el mensaje después de 5 segundos
      setTimeout(() => {
        successMessage.remove();
      }, 5000);

      // Navegar a la lista de MVPs
      navigate('/mvps');
    },
    onError: (error: Error) => {
      setApiError(error.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.mvpId?.trim()) {
      newErrors.mvpId = 'El ID del MVP es requerido';
    } else if (!/^[a-z0-9-]+$/.test(formData.mvpId)) {
      newErrors.mvpId = 'El ID solo puede contener letras minúsculas, números y guiones';
    }

    if (!formData.name?.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    if (!formData.description?.trim()) {
      newErrors.description = 'La descripción es requerida';
    }
    if (!formData.category?.trim()) {
      newErrors.category = 'La categoría es requerida';
    }
    if (!formData.targetAudience?.trim()) {
      newErrors.targetAudience = 'El público objetivo es requerido';
    }
    if (!formData.status) {
      newErrors.status = 'El estado es requerido';
    }
    if (formData.notificationEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.notificationEmail.trim())) {
      newErrors.notificationEmail = 'Email inválido';
    }
    if (formData.createdFrom && !/^https?:\/\/.+/.test(formData.createdFrom.trim())) {
      newErrors.createdFrom = 'URL inválida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      createMutation.mutate(formData);
    }
  };

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/mvps')}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-primary-600"
            >
              <ArrowLeft className="w-5 h-5" />
              Volver
            </button>
            <h1 className="text-2xl font-bold text-slate-900">
              Crear Nuevo MVP
            </h1>
          </div>
        </div>

        {/* API Error Message */}
        {apiError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">
                Error al crear el MVP
              </h3>
              <p className="mt-1 text-sm text-red-600">
                {apiError}
              </p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          {/* MVP ID */}
          <div>
            <label htmlFor="mvpId" className="block text-sm font-medium text-slate-700">
              ID del MVP
            </label>
            <input
              type="text"
              id="mvpId"
              value={formData.mvpId}
              onChange={(e) => setFormData(prev => ({ ...prev, mvpId: e.target.value }))}
              className={`
                mt-1 block w-full rounded-md shadow-sm sm:text-sm
                ${errors.mvpId
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500'
                }
              `}
              placeholder="mvp-nombre-001"
            />
            {errors.mvpId && (
              <p className="mt-1 text-sm text-red-600">{errors.mvpId}</p>
            )}
          </div>

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-700">
              Nombre
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className={`
                mt-1 block w-full rounded-md shadow-sm sm:text-sm
                ${errors.name
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500'
                }
              `}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-slate-700">
              Descripción
            </label>
            <textarea
              id="description"
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className={`
                mt-1 block w-full rounded-md shadow-sm sm:text-sm
                ${errors.description
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500'
                }
              `}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-slate-700">
              Categoría
            </label>
            <select
              id="category"
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className={`
                mt-1 block w-full rounded-md shadow-sm sm:text-sm
                ${errors.category
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500'
                }
              `}
            >
              <option value="">Selecciona una categoría</option>
              {CATEGORY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.category && (
              <p className="mt-1 text-sm text-red-600">{errors.category}</p>
            )}
          </div>

          {/* Target Audience */}
          <div>
            <label htmlFor="targetAudience" className="block text-sm font-medium text-slate-700">
              Público Objetivo
            </label>
            <textarea
              id="targetAudience"
              rows={3}
              value={formData.targetAudience}
              onChange={(e) => setFormData(prev => ({ ...prev, targetAudience: e.target.value }))}
              className={`
                mt-1 block w-full rounded-md shadow-sm sm:text-sm
                ${errors.targetAudience
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500'
                }
              `}
            />
            {errors.targetAudience && (
              <p className="mt-1 text-sm text-red-600">{errors.targetAudience}</p>
            )}
          </div>

          {/* Created From URL */}
          <div>
            <label htmlFor="createdFrom" className="block text-sm font-medium text-slate-700">
              URL de Origen (opcional)
            </label>
            <input
              type="url"
              id="createdFrom"
              value={formData.createdFrom}
              onChange={(e) => setFormData(prev => ({ ...prev, createdFrom: e.target.value }))}
              className={`
                mt-1 block w-full rounded-md shadow-sm sm:text-sm
                ${errors.createdFrom
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500'
                }
              `}
              placeholder="https://"
            />
            {errors.createdFrom && (
              <p className="mt-1 text-sm text-red-600">{errors.createdFrom}</p>
            )}
          </div>

          {/* Tags */}
          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-slate-700">
              Etiquetas
            </label>
            <div className="mt-1">
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 inline-flex items-center p-0.5 text-slate-400 hover:text-slate-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                className="block w-full rounded-md border-slate-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder="Presiona Enter para agregar una etiqueta"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-slate-700">
              Estado
            </label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className={`
                mt-1 block w-full rounded-md shadow-sm sm:text-sm
                ${errors.status
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500'
                }
              `}
            >
              <option value="">Selecciona un estado</option>
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.status && (
              <p className="mt-1 text-sm text-red-600">{errors.status}</p>
            )}
          </div>

          {/* Notification Email */}
          <div>
            <label htmlFor="notificationEmail" className="block text-sm font-medium text-slate-700">
              Email de Notificaciones (opcional)
            </label>
            <input
              type="email"
              id="notificationEmail"
              value={formData.notificationEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, notificationEmail: e.target.value }))}
              className={`
                mt-1 block w-full rounded-md shadow-sm sm:text-sm
                ${errors.notificationEmail
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500'
                }
              `}
              placeholder="ejemplo@dominio.com"
            />
            {errors.notificationEmail && (
              <p className="mt-1 text-sm text-red-600">{errors.notificationEmail}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/mvps')}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className={`
                inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg
                ${createMutation.isPending
                  ? 'bg-primary-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700'
                }
              `}
            >
              <Save className="w-4 h-4" />
              {createMutation.isPending ? 'Creando...' : 'Crear MVP'}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
} 